import { Route, Redirect, useLocation } from "react-router-dom"
import { useEffect, useState, useRef } from "react"
import { isLogin } from "config/environments"
import { PATH } from "constants/path"
import { useAppSelector } from "hooks/useApp"
import { checkUserInWorkspace } from "pages/organization/organization.services"
import { pushTo } from "helpers/history"
import {
  getWsCodeFromUrl,
  isRouteNotAllowedWithWsCode,
  removeWsCodePrefix,
  getSavedWsCode,
  saveWsCode,
  copyWsCodeToNewTab,
} from "helpers/wsCode.helper"
import LoadingCheckWorkspace from "components/LoadingCheckWorkspace/LoadingCheckWorkspace"
import { getTabId } from "components/DownloadProcess/downloadQueue"
import { useQuery } from "helpers/history"

const PrivateRoute = ({ component: Component, ...rest }) => {
  const location = useLocation()
  const { pathname, search } = location
  const query = useQuery()
  const userInfo = useAppSelector((state) => state.userInfo)

  useEffect(() => {
    const sourceTabId = query.get("tabId")
    if (sourceTabId) {
      const currentTabId = getTabId()
      if (sourceTabId !== currentTabId) {
        copyWsCodeToNewTab(sourceTabId, currentTabId)
      }

      // Xóa tabId khỏi URL sau khi đã copy data
      const newSearchParams = new URLSearchParams(search)
      newSearchParams.delete("tabId")
      const newSearch = newSearchParams.toString()
      window.history.replaceState(
        {},
        "",
        `${pathname}${newSearch ? `?${newSearch}` : ""}`
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const wsCodeFromUrl = getWsCodeFromUrl()
  const savedWsCode = getSavedWsCode()
  const routeNotAllowedWithWsCode = isRouteNotAllowedWithWsCode(pathname)
  const initialWsCodeCheckResult = routeNotAllowedWithWsCode
    ? true
    : wsCodeFromUrl && wsCodeFromUrl === savedWsCode && savedWsCode
    ? true
    : null

  const [isCheckingWsCode, setIsCheckingWsCode] = useState(false)
  const [wsCodeCheckResult, setWsCodeCheckResult] = useState<boolean | null>(
    initialWsCodeCheckResult
  )
  const hasCheckedRef = useRef<string>("")
  const isProcessingRef = useRef(false)

  const resetCheckingState = () => {
    setIsCheckingWsCode(false)
    isProcessingRef.current = false
  }

  const handleWorkspaceCheckResult = (
    result: {
      check: boolean
      last_organization_db_name?: string | null
      new_token: string
    },
    wsCodeToSave?: string,
    redirectPath?: string
  ) => {
    const hasAccess = result.check
    if (hasAccess && wsCodeToSave && result.new_token) {
      saveWsCode(wsCodeToSave, result.new_token)
      setWsCodeCheckResult(true)
      resetCheckingState()
      if (redirectPath) {
        pushTo(redirectPath, undefined, undefined, search)
      }
      return
    }
    if (result.last_organization_db_name && result.new_token) {
      const newWsCode = result.last_organization_db_name.toUpperCase()
      saveWsCode(newWsCode, result.new_token)
      setWsCodeCheckResult(true)
      resetCheckingState()
      const newPath = `/${newWsCode}${PATH.projects}`
      pushTo(newPath, undefined, undefined)
      return
    }

    setWsCodeCheckResult(false)
    resetCheckingState()
    pushTo(PATH.conversations)
  }

  const handleWorkspaceCheckError = (error: any) => {
    setWsCodeCheckResult(false)
    resetCheckingState()
    pushTo(PATH.conversations)
  }

  useEffect(() => {
    if (isRouteNotAllowedWithWsCode(pathname)) {
      if (wsCodeCheckResult !== true) {
        setWsCodeCheckResult(true)
      }
      isProcessingRef.current = false
      return
    }

    if (wsCodeFromUrl && wsCodeFromUrl === savedWsCode) {
      if (wsCodeCheckResult !== true) {
        setWsCodeCheckResult(true)
      }
      isProcessingRef.current = false
      const pathnameWithoutWsCode = removeWsCodePrefix(pathname)
      if (pathnameWithoutWsCode === "/") {
        pushTo(PATH.projects)
      }
      return
    }

    if (
      isProcessingRef.current ||
      isCheckingWsCode ||
      wsCodeCheckResult === true
    ) {
      return
    }

    // Tạo key để track việc check
    const currentCheckKey = `${
      wsCodeFromUrl || savedWsCode || "none"
    }_${pathname}`
    if (hasCheckedRef.current === currentCheckKey) {
      return
    }

    isProcessingRef.current = true
    hasCheckedRef.current = currentCheckKey

    let wsCodeToCheck: string
    let wsCodeToSave: string | undefined
    let redirectPath: string | undefined

    if (wsCodeFromUrl && wsCodeFromUrl !== savedWsCode) {
      wsCodeToCheck = wsCodeFromUrl
      wsCodeToSave = wsCodeFromUrl
    } else if (!wsCodeFromUrl && savedWsCode) {
      wsCodeToCheck = savedWsCode
      wsCodeToSave = savedWsCode
      const pathnameWithoutWsCode = removeWsCodePrefix(pathname)
      redirectPath = `/${savedWsCode.toUpperCase()}${pathnameWithoutWsCode}`
    } else {
      wsCodeToCheck = ""
      wsCodeToSave = undefined
    }

    setIsCheckingWsCode(true)
    setWsCodeCheckResult(null)
    checkUserInWorkspace(wsCodeToCheck)
      .then((result) => {
        handleWorkspaceCheckResult(result, wsCodeToSave, redirectPath)
      })
      .catch(handleWorkspaceCheckError)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsCodeFromUrl, savedWsCode, pathname, search])

  if (!isLogin()) {
    return (
      <Route
        {...rest}
        render={() => (
          <Redirect
            to={{
              pathname: PATH.login,
              state: { redirectUrl: pathname, search },
            }}
          />
        )}
      />
    )
  }
  if (!routeNotAllowedWithWsCode) {
    if (isCheckingWsCode || wsCodeCheckResult === null) {
      return <LoadingCheckWorkspace />
    }
    if (wsCodeCheckResult === false) {
      return <LoadingCheckWorkspace />
    }
  }

  // Check if user has last_organization_id
  // Only check if userInfo has been loaded (has id)
  // If not, only allow access to specific routes
  const isUserInfoLoaded = !!userInfo?.id
  const hasLastOrganizationId = !!userInfo?.last_organization_id

  // Routes allowed when user doesn't have last_organization_id
  const allowedRoutesWithoutOrg = [
    PATH.organizationAdd,
    PATH.general,
    PATH.updatePhoneNumberAndSecurityCode,
    PATH.enableEncryption,
    PATH.projectSensitiveData,
    PATH.conversations,
    PATH.inviteesPCB,
    PATH.inviteesBOM,
    PATH.inviteesMechComponent,
    PATH.inviteesOther,
    PATH.inviteesBuildPCB,
    PATH.inviteesBuildBOM,
    PATH.inviteesBuildMechComponent,
    PATH.inviteesBuildOther,
  ]

  // Check if current path is in allowed routes
  // For routes with parameters (like /invitees/component/:id/...), check if pathname starts with the base path
  const isAllowedRoute = allowedRoutesWithoutOrg.some((route) => {
    if (pathname === route) {
      return true
    }
    // For invitee routes with parameters, check if pathname starts with /invitees/component
    if (route.startsWith("/invitees/component")) {
      return pathname.startsWith("/invitees/component")
    }
    // For other routes, check if pathname includes the route
    return pathname.indexOf(route) > -1
  })

  // Only check last_organization_id if userInfo has been loaded
  // If user doesn't have last_organization_id and trying to access restricted route
  if (isUserInfoLoaded && !hasLastOrganizationId && !isAllowedRoute) {
    return (
      <Route
        {...rest}
        render={() => (
          <Redirect
            to={{
              pathname: PATH.conversations,
              state: { redirectUrl: pathname, search },
            }}
          />
        )}
      />
    )
  }
  return <Route {...rest} render={() => Component} />
}

export default PrivateRoute
